import React, { useCallback, useState, useEffect, SyntheticEvent } from 'react';
import { Modal, Button, Select, Input, Tooltip } from 'antd';
import { deleteInference } from '../../../lib/api/aws-api';
import { createTraining } from '../../../lib/api/training-api';
import { createModel, deleteModel, getLatestAlgorithmVersion } from '../../../lib/api/algorithm-api';
import { getLabelValue } from '../../../lib/util';
import { FormItemLabel, ModalSubTitle, ModalTitle } from '../../../components/text/Titles';
import UploadContainer from '../upload/UploadContainer';
import { InferenceFile, Model, Training } from '../../../types';
import { useGetAlgorithm } from '../../../lib/hooks';

const { Option } = Select;

type Props = {
  visible: boolean;
  onClose: () => any;
  onCreate: (model: Model, training: Training) => any;
  algorithmId: string;
};

const CreateModel = (props: Props) => {
  const { visible, onClose, onCreate, algorithmId } = props;

  const { algorithm } = useGetAlgorithm(algorithmId);
  const [annotationTypes, setAnnotationTypes] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [inferenceList, setInferenceList] = useState<InferenceFile[]>([]);
  const [model, setModel] = useState<null | Model>(null);
  const [nameValue, setNameValue] = useState<string>('');
  const [step, setStep] = useState<0 | 1>(0);

  const goToUploadEnabled = nameValue && selectedLabels.length > 0;
  const submitEnabled =
    inferenceList &&
    inferenceList.length > 0 &&
    inferenceList.every(inference => ['FINISHED', 'ERROR'].includes(inference.status));

  const rollbackCreation = useCallback(async () => {
    setModel(null);
    setInferenceList([]);
    if (!model) return;
    try {
      await deleteModel(model.id);
      await Promise.all(inferenceList.map(inference => deleteInference(inference.id)));
    } catch (e) {
      console.log(e);
    }
  }, [model, inferenceList]);

  useEffect(() => {
    if (algorithm) {
      setAnnotationTypes(algorithm.info.annotationTypes);
    }
    return () => {
      setAnnotationTypes([]);
    };
  }, [algorithm]);

  const onNameChange = (event: SyntheticEvent<HTMLInputElement>) => {
    setNameValue(event.currentTarget.value);
  };

  const onLabelChange = (value: string[]) => {
    setSelectedLabels(value);
  };

  const goToUpload = async () => {
    const version = await getLatestAlgorithmVersion(algorithmId);
    console.log('latest version has id ' + version.id);
    const model = await createModel(nameValue, algorithmId, version.id, selectedLabels);
    setModel(model);
    setStep(1);
  };

  const onSubmit = async () => {
    if (!model) return;

    const training = await createTraining(
      model.id,
      inferenceList.map(inference => inference.id),
      'Version 1.0'
    );
    onModalClose(false);
    onCreate(model, training);
  };

  const onModalClose = async (remove: boolean) => {
    if (remove && model) {
      await rollbackCreation();
    }

    onClose();

    algorithm && setSelectedLabels([]);
    setNameValue('');
    setStep(0);
  };

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={<ModalTitle>New model</ModalTitle>}
      onCancel={() => onModalClose(true)}
      footer={
        step === 0 ? (
          <div>
            <Button type="default" onClick={() => onModalClose(true)}>
              Cancel
            </Button>
            <Tooltip title={goToUploadEnabled ? '' : 'Enter a name and select at least 1 label before continuing.'}>
              <Button style={{ marginLeft: '10px' }} type="primary" onClick={goToUpload} disabled={!goToUploadEnabled}>
                Next
              </Button>
            </Tooltip>
          </div>
        ) : (
          <div>
            <Button type="default" onClick={() => onModalClose(true)}>
              Cancel
            </Button>
            <Tooltip title={submitEnabled ? '' : 'Upload at least 1 document and wait for them to finish analysing.'}>
              <Button type="primary" onClick={onSubmit} style={{ marginLeft: '10px' }} disabled={!submitEnabled}>
                Review
              </Button>
            </Tooltip>
          </div>
        )
      }>
      {step === 0 ? (
        <div>
          <FormItemLabel>This process will create a new model.</FormItemLabel>
          <Input placeholder="Name" value={nameValue} onChange={onNameChange} style={{ margin: '0px 0px 10px 0px' }} />
          <ModalSubTitle>Labels</ModalSubTitle>
          <FormItemLabel>Select the labels you’d like included in this model.</FormItemLabel>
          <Select
            value={selectedLabels}
            mode="tags"
            style={{ width: '100%', margin: '0px 0px 10px 0px' }}
            placeholder="Labels"
            onChange={onLabelChange}>
            {annotationTypes.map(annotation => (
              <Option key={annotation} value={annotation}>
                {getLabelValue(annotation)}
              </Option>
            ))}
          </Select>
        </div>
      ) : (
        <div>
          {model && (
            <div>
              <FormItemLabel>
                To create a custom model, we need some example documents. We advice you to upload and review a minimum
                of 3 documents.
              </FormItemLabel>
              <UploadContainer
                modelId={model.id}
                inferenceList={inferenceList}
                onInferenceListUpdated={inferenceList => setInferenceList(inferenceList)}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CreateModel;
